import HTMLFlipBook from "react-pageflip";
import { useEffect, useState, useRef } from "react";

function SecurePdfViewer({ url }) {
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [zoom, setZoom] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);

    const [isFullscreen, setIsFullscreen] = useState(false);
    const flipBookRef = useRef(null);
    const viewerRef = useRef(null);

    useEffect(() => {
        const handler = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener("fullscreenchange", handler);
        return () => document.removeEventListener("fullscreenchange", handler);
    }, []);

    const goToPrev = () => {
        if (flipBookRef.current) {
            flipBookRef.current.pageFlip().flipPrev();
        }
    };

    const goToNext = () => {
        if (flipBookRef.current) {
            flipBookRef.current.pageFlip().flipNext();
        }
    };

    useEffect(() => {
        if (!url) return;

        setLoading(true);
        setPages([]);
        setError(false);
        setCurrentPage(1);

        const loadPdf = async () => {
            try {
                if (!window.pdfjsLib) {
                    const script = document.createElement("script");
                    script.src =
                        "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";

                    await new Promise((resolve) => {
                        script.onload = resolve;
                        document.head.appendChild(script);
                    });

                    window.pdfjsLib.GlobalWorkerOptions.workerSrc =
                        "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
                }

                const response = await fetch(url, {
                    credentials: "include",
                    headers: {
                        "X-API-Key": process.env.REACT_APP_API_KEY,
                    },
                });

                if (!response.ok) {
                    throw new Error("PDF fetch failed: " + response.status);
                }

                const pdfData = await response.arrayBuffer();

                const pdf = await window.pdfjsLib.getDocument({
                    data: pdfData,
                }).promise;

                const renderedPages = [];

                for (let i = 1; i <= pdf.numPages; i++) {
                    const page = await pdf.getPage(i);

                    const viewport = page.getViewport({ scale: 1.5 });

                    const canvas = document.createElement("canvas");
                    const ctx = canvas.getContext("2d");

                    canvas.width = viewport.width;
                    canvas.height = viewport.height;

                    await page.render({
                        canvasContext: ctx,
                        viewport,
                    }).promise;

                    renderedPages.push(canvas.toDataURL("image/jpeg", 0.95));
                }

                setPages(renderedPages);
                setLoading(false);
            } catch (err) {
                console.error(err);
                setError(true);
                setLoading(false);
            }
        };

        loadPdf();
    }, [url]);

    if (loading) {
        return (
            <div className="h-[80vh] flex items-center justify-center">
                <div className="w-10 h-10 border-4 border-[#0B1E8A]/20 border-t-[#0B1E8A] rounded-full animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="h-[80vh] flex items-center justify-center text-red-500 font-semibold">
                PDF লোড করা যায়নি
            </div>
        );
    }

    return (
        <div
            ref={viewerRef}
            className="h-[80vh] flex flex-col bg-gradient-to-b from-slate-50 to-slate-200 rounded-2xl overflow-hidden p-4 pb-20 sm:pb-4"
            onContextMenu={(e) => e.preventDefault()}
            style={{ userSelect: "none", WebkitUserSelect: "none" }}
        >
            {/* Toolbar */}
            <div className="bg-white rounded-xl shadow-sm border px-4 py-3 mb-4 flex items-center justify-between flex-wrap gap-3">
                
                <div className="hidden sm:flex items-center gap-2">
                    <button
                        onClick={goToPrev}
                        className="px-4 py-2 bg-[#0B1E8A] text-white rounded-lg font-semibold hover:opacity-90 text-sm sm:text-base"
                    >
                        ← Previous
                    </button>
                    <button
                        onClick={goToNext}
                        className="px-4 py-2 bg-[#0B1E8A] text-white rounded-lg font-semibold hover:opacity-90 text-sm sm:text-base"
                    >
                        Next →
                    </button>
                </div>

                <div className="font-semibold text-slate-600 text-xs sm:text-sm">
                    Page {currentPage} / {pages.length}
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setZoom((z) => Math.max(0.8, z - 0.1))}
                        className="w-10 h-10 border rounded-lg hover:bg-slate-100 text-lg"
                    >
                        −
                    </button>
                    <span className="text-sm font-semibold min-w-[60px] text-center">
                        {Math.round(zoom * 100)}%
                    </span>
                    <button
                        onClick={() => setZoom((z) => Math.min(1.3, z + 0.1))}
                        className="w-10 h-10 border rounded-lg hover:bg-slate-100 text-lg"
                    >
                        +
                    </button>
                    <button
                        onClick={() => {
                            if (!document.fullscreenElement) {
                                viewerRef.current?.requestFullscreen();
                            } else {
                                document.exitFullscreen();
                            }
                        }}
                        className="px-3 py-2 border rounded-lg hover:bg-slate-100"
                    >
                        ⛶
                    </button>
                </div>
            </div>

            {/* FlipBook */}
            <div className="flex-1 flex justify-center items-center overflow-hidden">
                <HTMLFlipBook
                    ref={flipBookRef}
                    width={450}
                    height={650}
                    size="stretch"
                    minWidth={300}
                    maxWidth={700}
                    minHeight={400}
                    maxHeight={900}
                    drawShadow={true}
                    maxShadowOpacity={0.6}
                    showCover={true}
                    mobileScrollSupport={true}
                    flippingTime={600}
                    swipeDistance={30}
                    usePortrait={true}
                    startPage={0}
                    className="shadow-2xl"
                    onFlip={(e) => setCurrentPage(e.data + 1)}
                >
                    {pages.map((img, index) => (
                        <div
                            key={index}
                            className="bg-white flex items-center justify-center overflow-hidden"
                        >
                            <img
                                src={img}
                                alt={`Page ${index + 1}`}
                                draggable={false}
                                style={{
                                    transform: `scale(${zoom})`,
                                    transition: "transform .25s ease",
                                    transformOrigin: "center center",
                                }}
                                className="w-full h-full object-contain pointer-events-none"
                            />
                        </div>
                    ))}
                </HTMLFlipBook>
            </div>

            {/* Mobile bottom navigation — visible on mobile (< sm) OR during fullscreen */}
            <div className={`${isFullscreen ? '' : 'sm:hidden '}flex fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg px-4 py-3 items-center justify-center gap-4 z-50`}>
                <button
                    onClick={goToPrev}
                    className="flex-1 max-w-[140px] px-4 py-2.5 bg-[#0B1E8A] text-white rounded-lg font-semibold hover:opacity-90 text-sm"
                >
                    ← Previous
                </button>
                <button
                    onClick={goToNext}
                    className="flex-1 max-w-[140px] px-4 py-2.5 bg-[#0B1E8A] text-white rounded-lg font-semibold hover:opacity-90 text-sm"
                >
                    Next →
                </button>
            </div>
        </div>
    );
}

export default SecurePdfViewer;